import Handlebars from 'handlebars';
import { z } from 'zod';

/**
 * Takes an array of file names and barrels them all into a single
 * file using the provided template.
 *
 * @param files - array of file names
 * @param template - template to use for export syntax
 */
const createBarrelContent = (files, template) => {
    const exports = [];
    for (let f of files) {
        // Do not barrel variables files
        if (f.includes("variables"))
            continue;
        // Only barrel relevant files
        if (/\.[a-zA-Z0-9]+/.test(f) && !/\.([tj]s[x]?)$/.test(f))
            continue;
        const fArr = f.split(/[\\\/]/);
        const BASENAME = fArr[fArr.length - 1];
        const NAME = BASENAME.replace(/\.[a-z0-9]+$/i, "");
        const extArr = BASENAME.split(".");
        const EXTENSION = extArr[extArr.length - 1];
        let t = template;
        t = t.replace(/\{!basename\}/g, BASENAME);
        t = t.replace(/\{!name\}/g, NAME);
        t = t.replace(/\{!extension\}/g, EXTENSION);
        t = t.replace(/\{!path\}/g, f);
        exports.push(t);
    }
    return exports.join("\n");
};

/**
 * @internal
 *
 * Finds the differences (aka. missing tokens) between two Token arrays.
 */
const calculateMissingTokens = (unprocessedTokens, processedTokens) => {
    const missing = [];
    for (let i of unprocessedTokens) {
        let doesNotExist = true;
        for (let j of processedTokens) {
            if (doesNotExist && i.name === j.name) {
                doesNotExist = false;
            }
        }
        if (doesNotExist) {
            missing.push(i);
        }
    }
    return missing;
};

/**
 * @internal
 *
 * Takes a path to a YAML or JSON file and returns the name of the file.
 *
 * @param path a path to a file
 */
const getTokenName = (path) => {
    const type = /([a-zA-Z0-9\-\.]+)\.(?:ya?ml|json)/i.exec(path);
    if (!type) {
        return "";
    }
    return type[1];
};

/**
 * Extracts token variables from a token value.
 */
const extractVariables = (token) => {
    const vars = new Set();
    let tokenProcessed = token;
    while (/{![a-zA-Z0-9\-]+}/.test(tokenProcessed)) {
        const v = /{![a-zA-Z0-9\-]+}/.exec(tokenProcessed);
        vars.add(v[0]);
        tokenProcessed = tokenProcessed.replace(v[0], "");
    }
    return Array.from(vars);
};

/**
 * @internal
 *
 * Resolves non-unique tokens (overrides). The last instance of
 * a token will become the final value for that token name. Tokens
 * that are specified higher up in the import tree will, therefore,
 * be more specific than ones in files lower in the import tree.
 *
 * @param tokens an array of tokens
 */
const resolveOverrides = (tokens) => {
    const tokensUnique = {};
    for (let i = tokens.length - 1; i >= 0; i--) {
        const t = tokens[i];
        tokensUnique[t.name] = t;
    }
    const finalTokens = [];
    Object.keys(tokensUnique).map((key) => {
        finalTokens.push(tokensUnique[key]);
    });
    return finalTokens;
};

// Color Regular Expressions
const rgbRegex = /^rgb\(([0-9]{1,3}),[ ]?([0-9]{1,3}),[ ]?([0-9]{1,3})\)$/;
const rgbaRegex = /^rgba\(([0-9]{1,3}),[ ]?([0-9]{1,3}),[ ]?([0-9]{1,3}),[ ]?(0|1|1\.0+|0?\.[0-9]+)\)$/;
const hex3Regex = /^#([0-9a-f]{3})$/i;
const hex6Regex = /^#([0-9a-f]{6})$/i;
const hslRegex = /^hsl\(([0-9]{1,3}),[ ]?([0-9]{1,3})%,[ ]?([0-9]{1,3})%\)$/;
const hslaRegex = /^hsla\(([0-9]{1,3}),[ ]?([0-9]{1,3})%,[ ]?([0-9]{1,3})%,[ ]?(0|1|1\.0+|0?\.[0-9]+)\)$/;
const hsvRegex = /^hs[vb]\(([0-9]{1,3}),[ ]?([0-9]{1,3})%,[ ]?([0-9]{1,3})%\)$/;
const hsvaRegex = /^hs[vb]a\(([0-9]{1,3}),[ ]?([0-9]{1,3})%,[ ]?([0-9]{1,3})%,[ ]?(0|1|1\.0+|0?\.[0-9]+)\)$/;

/**
 * Convert a hex formatted color to rgb.
 *
 * @param hex - hex color string
 */
const hex2rgb = (hex) => {
    let value = "";
    if (hex6Regex.test(hex)) {
        const hex6 = hex6Regex.exec(hex) || [];
        value = hex6[1];
    }
    else if (hex3Regex.test(hex)) {
        const hex3 = hex3Regex.exec(hex) || [];
        const val = hex3[1];
        for (let c of val) {
            value += c.repeat(2);
        }
    }
    // Hex values
    const red_hex = value.slice(0, 2);
    const green_hex = value.slice(2, 4);
    const blue_hex = value.slice(4, 6);
    // Decimal values
    const red_dec = parseInt(red_hex, 16);
    const green_dec = parseInt(green_hex, 16);
    const blue_dec = parseInt(blue_hex, 16);
    return `rgb(${red_dec}, ${green_dec}, ${blue_dec})`;
};

/**
 * Convert an hsl color to hsv (hsb).
 *
 * @param hsl - an hsl color string
 */
const hsl2hsv = (hsl) => {
    var _a;
    const values = (_a = hslaRegex.exec(hsl)) !== null && _a !== void 0 ? _a : hslRegex.exec(hsl);
    if (!values) {
        throw Error(`Error parsing color: ${hsl}`);
    }
    const hue = parseInt(values[1], 10);
    const saturation = parseInt(values[2], 10) / 100;
    const lightness = parseInt(values[3], 10) / 100;
    const v = lightness + saturation * Math.min(lightness, 1 - lightness);
    const s = v === 0 ? 0 : 2 - (2 * lightness) / v;
    return `hsv(${hue}, ${Math.round(s * 100)}%, ${Math.round(v * 100)}%)`;
};

/**
 * Convert an hsl formatted color to rgb.
 *
 * @param hsl - hsl color string
 */
const hsl2rgb = (hsl) => {
    var _a;
    const values = (_a = hslaRegex.exec(hsl)) !== null && _a !== void 0 ? _a : hslRegex.exec(hsl);
    if (!values) {
        throw Error(`Error parsing color: ${hsl}`);
    }
    const hue = parseInt(values[1], 10);
    const saturation = parseInt(values[2], 10) / 100;
    const lightness = parseInt(values[3], 10) / 100;
    const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
    const m = lightness - c / 2;
    // Get colors
    let red, green, blue;
    if (hue < 60) {
        red = c;
        green = x;
        blue = 0;
    }
    else if (hue < 120) {
        red = x;
        green = c;
        blue = 0;
    }
    else if (hue < 180) {
        red = 0;
        green = c;
        blue = x;
    }
    else if (hue < 240) {
        red = 0;
        green = x;
        blue = c;
    }
    else if (hue < 300) {
        red = x;
        green = 0;
        blue = c;
    }
    else {
        red = c;
        green = 0;
        blue = x;
    }
    // Scale colors
    const r_prime = Math.round((red + m) * 255);
    const g_prime = Math.round((green + m) * 255);
    const b_prime = Math.round((blue + m) * 255);
    return `rgb(${r_prime}, ${g_prime}, ${b_prime})`;
};

/**
 * Convert an hsv/hsb color to hsl.
 *
 * @param hsv - an hsv or hsb color string
 */
const hsv2hsl = (hsv) => {
    var _a;
    const values = (_a = hsvaRegex.exec(hsv)) !== null && _a !== void 0 ? _a : hsvRegex.exec(hsv);
    if (!values) {
        throw Error(`Error parsing color: ${hsv}`);
    }
    const hue = parseInt(values[1], 10);
    const saturation = parseFloat(values[2]) / 100;
    const value = parseFloat(values[3]) / 100;
    const l = value - (value * saturation) / 2;
    const s = l === 0 || l === 1 ? 0 : (value - l) / Math.min(l, 1 - l);
    return `hsl(${hue}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
};

/**
 * Convert an rgb formatted color to hex.
 *
 * @param rgb - rgb color string
 */
const rgb2hex = (rgb) => {
    var _a;
    const values = (_a = rgbaRegex.exec(rgb)) !== null && _a !== void 0 ? _a : rgbRegex.exec(rgb);
    if (!values) {
        throw Error(`Error parsing color: ${rgb}`);
    }
    const red_dec = parseInt(values[1], 10);
    const green_dec = parseInt(values[2], 10);
    const blue_dec = parseInt(values[3], 10);
    // Hex values
    const red_hex = red_dec.toString(16);
    const green_hex = green_dec.toString(16);
    const blue_hex = blue_dec.toString(16);
    return `#${red_hex.length === 1 ? `0${red_hex}` : red_hex}${green_hex.length === 1 ? `0${green_hex}` : green_hex}${blue_hex.length === 1 ? `0${blue_hex}` : blue_hex}`;
};

/**
 * Convert an rgb formatted color to hsl.
 *
 * @param rgb - rgb color string
 */
const rgb2hsl = (rgb) => {
    var _a;
    const values = (_a = rgbaRegex.exec(rgb)) !== null && _a !== void 0 ? _a : rgbRegex.exec(rgb);
    if (!values) {
        throw Error(`Error parsing color: ${rgb}`);
    }
    const r = parseInt(values[1], 10);
    const g = parseInt(values[2], 10);
    const b = parseInt(values[3], 10);
    const r_prime = r / 255;
    const g_prime = g / 255;
    const b_prime = b / 255;
    const c_max = Math.max(r_prime, g_prime, b_prime);
    const c_min = Math.min(r_prime, g_prime, b_prime);
    const delta = c_max - c_min;
    // Find the hue
    let hue;
    if (delta === 0) {
        hue = 0;
    }
    else if (c_max === r_prime) {
        hue = (g_prime - b_prime) / delta;
        var shift = 0 / 60;
        if (hue < 0) {
            shift = 360 / 60;
        }
        hue = hue + shift;
    }
    else if (c_max === g_prime) {
        hue = (b_prime - r_prime) / delta + 2;
    }
    else if (c_max === b_prime) {
        hue = (r_prime - g_prime) / delta + 4;
    }
    else {
        hue = 0;
    }
    hue = 60 * hue;
    // Find the lightness
    const lightness = (c_max + c_min) / 2;
    // Find the saturation
    let saturation;
    if (delta !== 0) {
        saturation = delta / (1 - Math.abs(2 * lightness - 1));
    }
    else {
        saturation = 0;
    }
    return `hsl(${Math.round(hue)}, ${Math.round(saturation * 100)}%, ${Math.round(lightness * 100)}%)`;
};

/**
 * Convert an rgba formatted color to hsla.
 *
 * @param rgba - rgba color string
 */
const rgba2hsla = (rgba) => {
    var _a;
    const values = rgbaRegex.exec(rgba);
    if (!values) {
        throw Error(`Error parsing color: ${rgba}`);
    }
    const r = parseInt(values[1], 10);
    const g = parseInt(values[2], 10);
    const b = parseInt(values[3], 10);
    const a = parseFloat((_a = values[4]) !== null && _a !== void 0 ? _a : "1");
    const r_prime = r / 255;
    const g_prime = g / 255;
    const b_prime = b / 255;
    const c_max = Math.max(r_prime, g_prime, b_prime);
    const c_min = Math.min(r_prime, g_prime, b_prime);
    const delta = c_max - c_min;
    // Find the hue
    let hue;
    if (delta === 0) {
        hue = 0;
    }
    else if (c_max === r_prime) {
        hue = (g_prime - b_prime) / delta;
        var shift = 0 / 60;
        if (hue < 0) {
            shift = 360 / 60;
        }
        hue = hue + shift;
    }
    else if (c_max === g_prime) {
        hue = (b_prime - r_prime) / delta + 2;
    }
    else if (c_max === b_prime) {
        hue = (r_prime - g_prime) / delta + 4;
    }
    else {
        hue = 0;
    }
    hue = 60 * hue;
    // Find the lightness
    const lightness = (c_max + c_min) / 2;
    // Find the saturation
    let saturation;
    if (delta !== 0) {
        saturation = delta / (1 - Math.abs(2 * lightness - 1));
    }
    else {
        saturation = 0;
    }
    return `hsla(${Math.round(hue)}, ${Math.round(saturation * 100)}%, ${Math.round(lightness * 100)}%, ${a})`;
};

/**
 * Returns the color type of a color string.
 *
 * @param color - any color string
 */
const getColorFormat = (color) => {
    if (rgbRegex.test(color)) {
        return "rgb";
    }
    else if (rgbaRegex.test(color)) {
        return "rgba";
    }
    else if (hsvRegex.test(color)) {
        return "hsv";
    }
    else if (hsvaRegex.test(color)) {
        return "hsva";
    }
    else if (hslRegex.test(color)) {
        return "hsl";
    }
    else if (hslaRegex.test(color)) {
        return "hsla";
    }
    else if (hex3Regex.test(color) || hex6Regex.test(color)) {
        return "hex";
    }
    else {
        return "other";
    }
};

/**
 * Convert a color to hex format
 *
 * @param color - a color string
 */
const toHex = (color) => {
    const type = getColorFormat(color);
    let value = color;
    switch (type) {
        case "hex":
            value = color;
            break;
        case "rgb":
            value = rgb2hex(color);
            break;
        case "hsl":
            value = rgb2hex(hsl2rgb(color));
            break;
        case "hsv":
            value = rgb2hex(hsl2rgb(hsv2hsl(value)));
            break;
        case "rgba":
            const rgba = rgbaRegex.exec(color) || [];
            value = rgb2hex(`rgb(${rgba[1]}, ${rgba[2]}, ${rgba[3]})`);
            break;
        case "hsla":
            const hsla = hslaRegex.exec(color) || [];
            value = rgb2hex(hsl2rgb(`hsl(${hsla[1]}, ${hsla[2]}%, ${hsla[3]}%)`));
            break;
        case "hsva":
            const hsva = hsvaRegex.exec(color) || [];
            value = rgb2hex(hsl2rgb(hsv2hsl(`hsv(${hsva[1]}, ${hsva[2]}%, ${hsva[3]}%)`)));
            break;
    }
    return value;
};

/**
 * Convert a color to rgb format
 *
 * @param color - a color string
 */
const toRGB = (color) => {
    const type = getColorFormat(color);
    let value = color;
    switch (type) {
        case "hex":
            value = hex2rgb(color);
            break;
        case "rgb":
            value = color;
            break;
        case "hsl":
            value = hsl2rgb(color);
            break;
        case "hsv":
            value = hsl2rgb(hsv2hsl(value));
            break;
        case "rgba":
            const rgba = rgbaRegex.exec(color) || [];
            value = `rgb(${rgba[1]}, ${rgba[2]}, ${rgba[3]})`;
            break;
        case "hsla":
            const hsla = hslaRegex.exec(color) || [];
            value = hsl2rgb(`hsl(${hsla[1]}, ${hsla[2]}%, ${hsla[3]}%)`);
            break;
        case "hsva":
            const hsva = hsvaRegex.exec(color) || [];
            value = hsl2rgb(hsv2hsl(`hsv(${hsva[1]}, ${hsva[2]}%, ${hsva[3]}%)`));
            break;
    }
    return value;
};

/**
 * Convert a color to rgba format
 *
 * @param color - a color string
 */
const toRGBA = (color) => {
    var _a, _b;
    const type = getColorFormat(color);
    let value = color;
    const rgb2rgba = (color, alpha) => {
        const rgb = rgbRegex.exec(toRGB(color));
        return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha !== null && alpha !== void 0 ? alpha : 1})`;
    };
    switch (type) {
        case "hex":
            value = rgb2rgba(hex2rgb(color));
            break;
        case "rgb":
            value = rgb2rgba(color);
            break;
        case "hsl":
            value = rgb2rgba(hsl2rgb(color));
            break;
        case "hsv":
            value = rgb2rgba(hsl2rgb(hsv2hsl(color)));
            break;
        case "rgba":
            value = color;
            break;
        case "hsva":
            const hsva = hsvaRegex.exec(color);
            value = rgb2rgba(hsl2rgb(hsv2hsl(color)), parseFloat((_a = hsva === null || hsva === void 0 ? void 0 : hsva[4]) !== null && _a !== void 0 ? _a : "1"));
            break;
        case "hsla":
            const hsla = hslaRegex.exec(color);
            value = rgb2rgba(hsl2rgb(color), parseFloat((_b = hsla === null || hsla === void 0 ? void 0 : hsla[4]) !== null && _b !== void 0 ? _b : "1"));
            break;
    }
    return value;
};

const convertAlpha = (alpha) => {
    if (!alpha)
        return "FF";
    const alphaHex = Math.round(parseFloat(alpha) * 255).toString(16);
    return alphaHex.length === 1 ? `0${alphaHex}` : alphaHex;
};
/**
 * Convert a color to hex format
 *
 * @param color - a color string
 */
const toHexAlpha = (color) => {
    const type = getColorFormat(color);
    let value = color;
    switch (type) {
        case "hex":
            value = color;
            break;
        case "rgb":
            value = rgb2hex(color);
            break;
        case "hsl":
            value = rgb2hex(hsl2rgb(color));
            break;
        case "hsv":
            value = rgb2hex(hsl2rgb(hsv2hsl(value)));
            break;
        case "rgba":
            const rgba = rgbaRegex.exec(color) || [];
            value = rgb2hex(toRGBA(color)) + convertAlpha(rgba[4]);
            break;
        case "hsla":
            const hsla = hslaRegex.exec(color) || [];
            value = rgb2hex(toRGBA(color)) + convertAlpha(hsla[4]);
            break;
        case "hsva":
            const hsva = hsvaRegex.exec(color) || [];
            value = rgb2hex(toRGBA(color)) + convertAlpha(hsva[4]);
            break;
    }
    return value;
};

/**
 * Convert a color to hsl format
 *
 * @param color - a color string
 */
const toHSL = (color) => {
    const type = getColorFormat(color);
    let value = color;
    switch (type) {
        case "hex":
            value = rgb2hsl(hex2rgb(color));
            break;
        case "rgb":
            value = rgb2hsl(color);
            break;
        case "hsl":
            value = color;
            break;
        case "hsv":
            value = hsv2hsl(color);
            break;
        case "rgba":
            const rgba = rgbaRegex.exec(color) || [];
            value = rgb2hsl(`rgb(${rgba[1]}, ${rgba[2]}, ${rgba[3]})`);
            break;
        case "hsla":
            const hsla = hslaRegex.exec(color) || [];
            value = `hsl(${hsla[1]}, ${hsla[2]}%, ${hsla[3]}%)`;
            break;
        case "hsva":
            const hsva = hsvaRegex.exec(color) || [];
            value = hsv2hsl(`hsv(${hsva[1]}, ${hsva[2]}%, ${hsva[3]}%)`);
            break;
    }
    return value;
};

/**
 * Retrieve the various attributes of the color provided in
 * object format. This is useful when needing to manipulate
 * specific properties of a color.
 *
 * @exmaple
 * ```
 * const { red, green, blue } = getColorObject<RGBObject>('rgb(180, 30, 240)');
 * const {
 *  hue,
 *  saturation,
 *  lightness,
 *  alpha
 * } = getColorObject<HSLAObject>('hsla(0, 0%, 100%, 0.25)');
 * ```
 */
const getColorObject = (color) => {
    let o;
    switch (getColorFormat(color)) {
        case "hex": {
            let hexArr;
            if (hex3Regex.test(color)) {
                hexArr = hex3Regex.exec(color);
            }
            else {
                hexArr = hex6Regex.exec(color);
            }
            if (hexArr) {
                let hex = hexArr[1];
                if (hexArr[1].length === 3) {
                    hex = hexArr[1]
                        .split("")
                        .map((c) => c.repeat(2))
                        .join("");
                }
                o = {
                    red: hex.substring(0, 2),
                    green: hex.substring(2, 4),
                    blue: hex.substring(4, 6),
                };
                break;
            }
        }
        case "rgb": {
            const rgb = rgbRegex.exec(color);
            if (rgb) {
                const red = parseInt(rgb[1], 10);
                const green = parseInt(rgb[2], 10);
                const blue = parseInt(rgb[3], 10);
                o = { red, green, blue };
                break;
            }
        }
        case "rgba": {
            const rgba = rgbaRegex.exec(color);
            if (rgba) {
                const red = parseInt(rgba[1], 10);
                const green = parseInt(rgba[2], 10);
                const blue = parseInt(rgba[3], 10);
                const alpha = parseFloat(rgba[4]);
                o = { red, green, blue, alpha };
                break;
            }
        }
        case "hsl": {
            const hsl = hslRegex.exec(color);
            if (hsl) {
                const hue = parseInt(hsl[1], 10);
                const saturation = parseInt(hsl[2], 10);
                const lightness = parseInt(hsl[3], 10);
                o = { hue, saturation, lightness };
                break;
            }
        }
        case "hsla": {
            const hsla = hslaRegex.exec(color);
            if (hsla) {
                const hue = parseInt(hsla[1], 10);
                const saturation = parseInt(hsla[2], 10);
                const lightness = parseInt(hsla[3], 10);
                const alpha = parseFloat(hsla[4]);
                o = { hue, saturation, lightness, alpha };
                break;
            }
        }
        case "hsv": {
            const hsv = hsvRegex.exec(color);
            if (hsv) {
                const hue = parseFloat(hsv[1]);
                const saturation = parseFloat(hsv[2]);
                const value = parseFloat(hsv[3]);
                o = { hue, saturation, value };
                break;
            }
        }
        case "hsva": {
            const hsva = hsvaRegex.exec(color);
            if (hsva) {
                const hue = parseFloat(hsva[1]);
                const saturation = parseFloat(hsva[2]);
                const value = parseFloat(hsva[3]);
                const alpha = parseFloat(hsva[4]);
                o = { hue, saturation, value, alpha };
                break;
            }
        }
        default:
            throw Error(`Color cannot be converted to an object: ${color}`);
    }
    return o;
};

/**
 * Apply/override an alpha channel for a color.
 *
 * @param color - any color to add/override an alpha channel to
 * @param options.value - the alpha to apply
 */
const setAlpha = (color, { value: alpha = 1 }) => {
    const { hue, saturation, lightness } = getColorObject(toHSL(color));
    return `hsla(${hue}, ${saturation}%, ${lightness}%, ${Math.max(Math.min(alpha, 1), 0)})`;
};

/**
 * Convert a color to hsla format
 *
 * @param color - a color string
 */
const toHSLA = (color) => {
    var _a, _b, _c;
    const type = getColorFormat(color);
    let value = color;
    switch (type) {
        case "hex":
            value = setAlpha(toHSL(color), { value: 1 });
            break;
        case "rgb":
            value = setAlpha(toHSL(color), { value: 1 });
            break;
        case "hsl":
            value = setAlpha(color, { value: 1 });
            break;
        case "hsv":
            value = setAlpha(toHSL(color), { value: 1 });
            break;
        case "rgba":
            const rgba = rgbaRegex.exec(color);
            value = setAlpha(toHSL(color), { value: parseFloat((_a = rgba === null || rgba === void 0 ? void 0 : rgba[4]) !== null && _a !== void 0 ? _a : "1") });
            break;
        case "hsla":
            const hsla = hslaRegex.exec(color);
            value = setAlpha(toHSL(color), { value: parseFloat((_b = hsla === null || hsla === void 0 ? void 0 : hsla[4]) !== null && _b !== void 0 ? _b : "1") });
            break;
        case "hsva":
            const hsva = hsvaRegex.exec(color);
            value = setAlpha(toHSL(color), { value: parseFloat((_c = hsva === null || hsva === void 0 ? void 0 : hsva[4]) !== null && _c !== void 0 ? _c : "1") });
            break;
    }
    return value;
};

const _adjust = (property, amount, max, circular) => {
    const val = Math.max(Math.min(property + Math.round(amount), max), 0);
    return val >= max && circular ? 0 : val;
};
/**
 * Adjust the red, green, or blue properties of a color.
 *
 * @param color a color in any format
 * @param options.property property that should be adjusted
 * @param options.value amount to adjust the property by
 */
const adjustRGB = (color, { property, value }) => {
    const adjustments = {
        red: property === "red" ? value : 0,
        green: property === "green" ? value : 0,
        blue: property === "blue" ? value : 0,
    };
    const { red, green, blue, alpha } = getColorObject(toRGBA(color));
    return rgba2hsla(`rgba(${_adjust(red, adjustments.red, 255)}, ${_adjust(green, adjustments.green, 255)}, ${_adjust(blue, adjustments.blue, 255)}, ${alpha})`);
};
/**
 * Adjust the hue, saturation, or lightness properties of a color.
 *
 * @param color a color in any format
 * @param options.property property that should be adjusted
 * @param options.value amount to adjust property by
 */
const adjustHSL = (color, { value, property }) => {
    const adjustments = {
        hue: property === "hue" ? value : 0,
        saturation: property === "saturation" ? value : 0,
        lightness: property === "lightness" ? value : 0,
    };
    const { hue, saturation, lightness, alpha } = getColorObject(toHSLA(color));
    return `hsla(${_adjust(hue, adjustments.hue, 360, true)}, ${_adjust(saturation, adjustments.saturation, 100)}%, ${_adjust(lightness, adjustments.lightness, 100)}%, ${alpha})`;
};
/**
 * Adjust the hue, saturation, or lightness properties of a color.
 *
 * @param color a color in any format
 * @param options.property property that should be adjusted
 * @param options.value amount to adjust property by
 */
const adjustHSV = (color, { value: valueParam, property }) => {
    const adjustments = {
        hue: property === "hue" ? valueParam : 0,
        saturation: property === "saturation" ? valueParam : 0,
        value: property === "value" ? valueParam : 0,
    };
    const { hue, saturation, value, alpha } = getColorObject(hsl2hsv(toHSLA(color)));
    return setAlpha(hsv2hsl(`hsv(${_adjust(hue, adjustments.hue, 360, true)}, ${_adjust(saturation, adjustments.saturation, 100)}%, ${_adjust(value, adjustments.value, 100)}%)`), { value: alpha });
};
const adjust = (color, options) => {
    switch (options.property) {
        case "value":
        case "lightness":
        case "hue":
        case "saturation":
            const type = getColorFormat(color);
            if (type === "hsv" || type === "hsva" || options.property === "value") {
                return adjustHSV(color, options);
            }
            else {
                return adjustHSL(color, options);
            }
        case "red":
        case "green":
        case "blue":
            return adjustRGB(color, options);
        default:
            return color;
    }
};

/**
 * Applies an adjustment to a color based on the HSV/B or HSL color space.
 * By default this function will increase the delta. Setting `invert` to true will decrease the delta.
 *
 * @param color - color to adjust
 * @param options.background - color of the background
 * @param options.amount - amount to adjust the luminance [0, 100]
 * @param options.invert - whether to decrease the luminance
 * @param options.useHSL - use the HSL color space for the transformation
 *
 * @returns color with adjusted luminance
 *
 * @see https://en.wikipedia.org/wiki/HSL_and_HSV#Interconversion
 */
const adjustLumaDelta = (color, options) => {
    const { background, amount, invert = false, useHSL = false } = options;
    // Convert color to HSL
    const colorHSL = toHSLA(color);
    const colorHSLObject = getColorObject(toHSLA(color));
    const backgroundHSL = toHSLA(background);
    // Convert HSL color to HSV
    const colorHSV = getColorObject(hsl2hsv(colorHSL));
    const backgroundHSV = getColorObject(hsl2hsv(backgroundHSL));
    // Check if the background is light or dark
    const lightBackground = backgroundHSV.value >= 50;
    const fractionalAmount = amount;
    // Calculate the adjustment
    const delta = lightBackground ? -fractionalAmount : fractionalAmount;
    const adjustment = invert ? -delta : delta;
    // Make the adjustment to the color, then convert HSV color back to HSL
    // if necessary because browsers support HSL and make sure values don't
    // escape proper bounds.
    const hsl = useHSL
        ? `hsl(${colorHSLObject.hue}, ${colorHSLObject.saturation}%, ${Math.max(0, Math.min(100, Math.round(colorHSLObject.lightness + adjustment)))}%)`
        : hsv2hsl(`hsv(${colorHSV.hue},${colorHSV.saturation}%,${Math.max(0, Math.min(100, Math.round(colorHSV.value + adjustment)))}%)`);
    return setAlpha(hsl, { value: colorHSLObject.alpha });
};

/**
 * Blend two colors together by applying a non-opaque foreground
 * color on top of an opaque background color. The returned color
 * will be the result of performing a "color pick" from the blended
 * colors.
 *
 * @param foreground - an opaque background color (if an alpha channel
 * is found it will be removed)
 * @param options.background - an opaque background color (if an alpha channel
 * is found it will be removed)
 * @param options.alpha - the amount of alpha to use when blending the colors [0, 1]
 *
 * @returns an opaque, alpha blended color
 */
const alphaBlend = (foreground, { background, alpha }) => {
    const { red: redBG, green: greenBG, blue: blueBG, } = getColorObject(toRGB(background));
    const { red: redFG, green: greenFG, blue: blueFG, } = getColorObject(toRGB(foreground));
    const r = Math.round(redBG * (1 - alpha) + redFG * alpha);
    const g = Math.round(greenBG * (1 - alpha) + greenFG * alpha);
    const b = Math.round(blueBG * (1 - alpha) + blueFG * alpha);
    return toHSL(`rgb(${r}, ${g}, ${b})`);
};
/**
 * This function will perform an alpha blend using the `alphaBlend`
 * function. This function is a utility to more accurately match
 * graph terminology.
 *
 * @param foreground - an opaque background color (if an alpha channel
 * is found it will be removed)
 * @param options.background - an opaque background color (if an alpha channel
 * is found it will be removed)
 * @param options.alpha - the amount of alpha to use when blending the colors [0, 1]
 *
 * @returns an opaque, alpha blended color
 */
const interpolate = alphaBlend;

const _change = (value, max, circular) => {
    const val = Math.max(Math.min(Math.round(value), max), 0);
    return val >= max && circular ? 0 : val;
};
/**
 * Change the red, green, or blue properties of a color.
 *
 * @param color a color in any format
 * @param options.property property that should be changed
 * @param options.value value to change property to
 */
const changeRGB = (color, { value, property }) => {
    const { red, green, blue, alpha } = getColorObject(toRGBA(color));
    const changes = {
        red: property === "red" ? value : red,
        green: property === "green" ? value : green,
        blue: property === "blue" ? value : blue,
    };
    return rgba2hsla(`rgba(${_change(changes.red, 255)}, ${_change(changes.green, 255)}, ${_change(changes.blue, 255)}, ${alpha})`);
};
/**
 * Change the hue, saturation, or lightness properties of a color.
 *
 * @param color a color in any format
 * @param options.property property that should be changed
 * @param options.value value to change property to
 */
const changeHSL = (color, { property, value }) => {
    const { hue, saturation, lightness, alpha } = getColorObject(toHSLA(color));
    const changes = {
        hue: property === "hue" ? value : hue,
        saturation: property === "saturation" ? value : saturation,
        lightness: property === "lightness" ? value : lightness,
    };
    return `hsla(${_change(changes.hue, 360, true)}, ${_change(changes.saturation, 100)}%, ${_change(changes.lightness, 100)}%, ${alpha})`;
};
/**
 * Change the hue, saturation, or value properties of a color.
 *
 * @param color a color in any format
 * @param options.property property that should be changed
 * @param options.value value to change property to
 */
const changeHSV = (color, { property, value: valueParam }) => {
    const { hue, saturation, value, alpha } = getColorObject(hsl2hsv(toHSLA(color)));
    const changes = {
        hue: property === "hue" ? valueParam : hue,
        saturation: property === "saturation" ? valueParam : saturation,
        value: property === "value" ? valueParam : value,
    };
    return setAlpha(hsv2hsl(`hsv(${_change(changes.hue, 360, true)}, ${_change(changes.saturation, 100)}%, ${_change(changes.value, 100)}%)`), { value: alpha });
};
const change = (color, options) => {
    switch (options.property) {
        case "value":
        case "lightness":
        case "hue":
        case "saturation":
            const type = getColorFormat(color);
            if (type === "hsv" || type === "hsva" || options.property === "value") {
                return changeHSV(color, options);
            }
            else {
                return changeHSL(color, options);
            }
        case "red":
        case "green":
        case "blue":
            return changeRGB(color, options);
        default:
            return color;
    }
};

/**
 * Darkens a color by a specified percentage.
 *
 * @param color - any color string
 * @param options.value - percent to lighten the color by
 */
const darken = (color, { value: percentage = 0 }) => {
    const { red, green, blue, alpha } = getColorObject(toRGBA(color));
    const darkenBounded = Math.max(Math.min(percentage, 1), 0);
    // Scale the colors
    const red_scaled = Math.round(Math.min(Math.max(0, red + (0 - red) * darkenBounded), 255));
    const green_scaled = Math.round(Math.min(Math.max(0, green + (0 - green) * darkenBounded), 255));
    const blue_scaled = Math.round(Math.min(Math.max(0, blue + (0 - blue) * darkenBounded), 255));
    return toHSLA(`rgba(${red_scaled},${green_scaled},${blue_scaled}, ${alpha})`);
};

/**
 * Grayscale a color while keeping the same (relative) luminance.
 *
 * @param color - the color to grayscale
 *
 * @returns the grayscaled color
 *
 * @see https://en.wikipedia.org/wiki/Grayscale#Luma_coding_in_video_systems
 */
const grayscale = (color, options) => {
    const { red, green, blue, alpha } = getColorObject(toRGBA(color));
    let avg = Math.round(red * 0.299 + green * 0.587 + blue * 0.114);
    if (options.method === "average") {
        avg = Math.round((red + green + blue) / 3);
    }
    else if (options.method === "lightness") {
        avg = Math.round((Math.max(red, green, blue) + Math.min(red, green, blue)) / 2);
    }
    return setAlpha(toHSLA(`rgb(${avg}, ${avg}, ${avg})`), { value: alpha });
};

/**
 * Decreases the contrast between a color and background by a specified amount.
 *
 * @param color - color to adjust
 * @param options.background - color of the background
 * @param options.amount - amount to adjust the luminance [0, 1]
 *
 * @returns the color with its contrast decreased
 */
const decreaseContrast = (color, options) => {
    const grayscaleBackground = grayscale(options.background, {
        method: "luminosity",
    });
    return alphaBlend(color, {
        background: grayscaleBackground,
        alpha: options.amount,
    });
};

/**
 * Decreases the luminance delta of a color and its background based on its HSV/HSB representation.
 *
 * @param color - color to adjust
 * @param options.background - color of the background
 * @param options.amount - amount to adjust the luminance [0, 1]
 * @param options.useHSL - use the HSL color space for the transformation
 *
 * @returns color with decreased luminance
 *
 * @see https://en.wikipedia.org/wiki/HSL_and_HSV#Interconversion
 */
const decreaseLumaDelta = (color, options) => {
    return adjustLumaDelta(color, { ...options, invert: true });
};

/**
 * Increases the luminance delta of a color and it's background based on its HSV/HSB representation.
 *
 * @param color - color to adjust
 * @param options.background - color of the background
 * @param options.amount - amount to adjust the luminance [0, 100]
 * @param options.useHSL - use the HSL color space for the transformation
 *
 * @returns color with increased luminance
 *
 * @see https://en.wikipedia.org/wiki/HSL_and_HSV#Interconversion
 */
const increaseLumaDelta = (color, options) => {
    return adjustLumaDelta(color, { ...options, invert: false });
};

/**
 * Inverts the luminance of a color in the HSL color space
 *
 * @param color: string - color to invert luminance
 * @param options.useHSL - use the HSL color space for the transformation
 */
const invertLuminance = (color, options) => {
    if (options.useHSL) {
        const { hue, saturation, lightness, alpha } = getColorObject(toHSLA(color));
        const l = 100 - lightness;
        return setAlpha(`hsl(${hue}, ${saturation}%, ${l}%)`, { value: alpha });
    }
    const { hue, saturation, value, alpha } = getColorObject(hsl2hsv(toHSLA(color)));
    const v = 100 - value;
    return setAlpha(toHSL(`hsv(${hue}, ${saturation}%, ${v}%)`), {
        value: alpha,
    });
};

/**
 * Lightens a color by a specified percentage.
 *
 * Note: Attempts to lighten the color black will result in getting the color black.
 *
 * @param color - any color string
 * @param options.value - percent to lighten the color by
 */
const lighten = (color, { value: percentage = 0 }) => {
    const { red, green, blue, alpha } = getColorObject(toRGBA(color));
    const lightenBounded = Math.max(Math.min(percentage, 1), 0);
    // Scale the colors
    const red_scaled = Math.round(Math.min(Math.max(0, red + (255 - red) * lightenBounded), 255));
    const green_scaled = Math.round(Math.min(Math.max(0, green + (255 - green) * lightenBounded), 255));
    const blue_scaled = Math.round(Math.min(Math.max(0, blue + (255 - blue) * lightenBounded), 255));
    return toHSLA(`rgba(${red_scaled},${green_scaled},${blue_scaled}, ${alpha})`);
};

/**
 * Calculates the luma (perceptual brightness) of a color.
 *
 * @param color - color to calculate luma
 *
 * @see https://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
 */
const relativeLuminance = (color) => {
    const { red, green, blue } = getColorObject(toRGBA(color));
    const determineChannelRelativeLuminance = (value) => {
        // convert the RGB value to a fraction
        let newValue = value / 255;
        if (newValue <= 0.03928) {
            newValue = newValue / 12.92;
        }
        else {
            newValue = Math.pow((newValue + 0.055) / 1.055, 2.4);
        }
        return newValue;
    };
    return (determineChannelRelativeLuminance(red) * 0.2126 +
        determineChannelRelativeLuminance(green) * 0.7152 +
        determineChannelRelativeLuminance(blue) * 0.0722);
};

/**
 * Given an array of colors and a background, returns the color with the greatest contrast.
 *
 * @param background - color of the background
 * @param options.colors - array of colors to compare to the background.
 */
const maxLumaContrast = (background, options) => {
    const contrasts = options.colors.map((color) => {
        const l1 = relativeLuminance(color);
        const l2 = relativeLuminance(background);
        const contrast = (Math.min(l1, l2) + 0.05) / (Math.max(l1, l2) + 0.05);
        return contrast;
    });
    // Contrast is represented as a ratio so a lower
    // number equates to a higher contrast
    const maxContrast = Math.min(...contrasts);
    return toHSLA(options.colors[contrasts.indexOf(maxContrast)]);
};

const _scale = (property, percentage, max, circular) => {
    const val = Math.max(Math.min(Math.round(property + percentage * (percentage > 0 ? max - property : property)), max), 0);
    return val >= max && circular ? 0 : val;
};
/**
 * Scale the red, green, or blue properties of a color.
 *
 * @param color a color in any format
 * @param options.property property that should be scaled
 * @param options.value percentage to scale `property` by
 */
const scaleRGB = (color, { value, property }) => {
    const scalars = {
        red: property === "red" ? value : 0,
        green: property === "green" ? value : 0,
        blue: property === "blue" ? value : 0,
    };
    const { red, green, blue, alpha } = getColorObject(toRGBA(color));
    return rgba2hsla(`rgba(${_scale(red, scalars.red, 255)}, ${_scale(green, scalars.green, 255)}, ${_scale(blue, scalars.blue, 255)}, ${alpha})`);
};
/**
 * Scale the hue, saturation, or lightness properties of a color.
 *
 * @param color a color in any format
 * @param options.property property that should be scaled
 * @param options.value percentage to scale `property` by
 */
const scaleHSL = (color, { value, property }) => {
    const scalars = {
        hue: property === "hue" ? value : 0,
        saturation: property === "saturation" ? value : 0,
        lightness: property === "lightness" ? value : 0,
    };
    const { hue, saturation, lightness, alpha } = getColorObject(toHSLA(color));
    return `hsla(${_scale(hue, scalars.hue, 360, true)}, ${_scale(saturation, scalars.saturation, 100)}%, ${_scale(lightness, scalars.lightness, 100)}%, ${alpha})`;
};
/**
 * Scale the hue, saturation, or lightness properties of a color.
 *
 * @param color a color in any format
 * @param options.property property that should be scaled
 * @param options.value percentage to scale `property` by
 */
const scaleHSV = (color, { value: valueParam, property }) => {
    const scalars = {
        hue: property === "hue" ? valueParam : 0,
        saturation: property === "saturation" ? valueParam : 0,
        value: property === "value" ? valueParam : 0,
    };
    const { hue, saturation, value, alpha } = getColorObject(hsl2hsv(toHSLA(color)));
    return setAlpha(hsl2hsv(`hsl(${_scale(hue, scalars.hue, 360, true)}, ${_scale(saturation, scalars.saturation, 100)}%, ${_scale(value, scalars.value, 100)}%)`), { value: alpha });
};
const scale = (color, options) => {
    switch (options.property) {
        case "value":
        case "lightness":
        case "hue":
        case "saturation":
            const type = getColorFormat(color);
            if (type === "hsv" || type === "hsva" || options.property === "value") {
                return scaleHSV(color, options);
            }
            else {
                return scaleHSL(color, options);
            }
        case "red":
        case "green":
        case "blue":
            return scaleRGB(color, options);
        default:
            return color;
    }
};

/**
 * Color transformations will yield HSLA colors.
 */
const Color = {
    convert: {
        hex2rgb,
        hsl2hsv,
        hsl2rgb,
        hsv2hsl,
        rgb2hex,
        rgb2hsl,
        rgba2hsla,
        toHex,
        toHexAlpha,
        toHSL,
        toHSLA,
        toRGB,
        toRGBA,
    },
    lighten,
    darken,
    adjust,
    setAlpha,
    change,
    scale,
    getColorObject,
    alphaBlend,
    interpolate,
    adjustLumaDelta,
    increaseLumaDelta,
    decreaseLumaDelta,
    decreaseContrast,
    grayscale,
    maxLumaContrast,
    invertLuminance,
    relativeLuminance,
};

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    Color: Color,
    adjust: adjust,
    adjustLumaDelta: adjustLumaDelta,
    alphaBlend: alphaBlend,
    change: change,
    darken: darken,
    decreaseContrast: decreaseContrast,
    decreaseLumaDelta: decreaseLumaDelta,
    getColorFormat: getColorFormat,
    grayscale: grayscale,
    hex2rgb: hex2rgb,
    hex3Regex: hex3Regex,
    hex6Regex: hex6Regex,
    hsl2hsv: hsl2hsv,
    hsl2rgb: hsl2rgb,
    hslRegex: hslRegex,
    hslaRegex: hslaRegex,
    hsv2hsl: hsv2hsl,
    hsvRegex: hsvRegex,
    hsvaRegex: hsvaRegex,
    increaseLumaDelta: increaseLumaDelta,
    interpolate: interpolate,
    invertLuminance: invertLuminance,
    lighten: lighten,
    maxLumaContrast: maxLumaContrast,
    relativeLuminance: relativeLuminance,
    rgb2hex: rgb2hex,
    rgb2hsl: rgb2hsl,
    rgbRegex: rgbRegex,
    rgba2hsla: rgba2hsla,
    rgbaRegex: rgbaRegex,
    scale: scale,
    setAlpha: setAlpha,
    toHSL: toHSL,
    toHSLA: toHSLA,
    toHex: toHex,
    toHexAlpha: toHexAlpha,
    toRGB: toRGB,
    toRGBA: toRGBA
});

/**
 * @internal
 *
 * Apply transformations to the token value. Transformations will be applied in
 * the order that they are specified in the array.
 *
 * @param token.value token's initial value
 * @param token.transform an array of transforms to apply to `token.value`
 * @param customTransformers custom transformation functions that may show up in `token.transform`
 */
const resolveTransformation = ({ value, derivation }, customTransformers = {}) => {
    // Just return if there isn't a valid transform
    if (!derivation || !derivation.transform || derivation.transform.length === 0)
        return value;
    // Set up transformers
    const transformers = { ...DefaultTransformer, ...customTransformers };
    // Transform the value
    let transformedValue = value;
    derivation.transform.forEach((t) => {
        Object.entries(t).forEach(([key, options]) => {
            if (!transformers.hasOwnProperty(key)) {
                throw Error(`Attempted to transform '${value}' using a missing transformer: \`${key}\`.`);
            }
            transformedValue = transformers[key](transformedValue, options);
        });
    });
    return transformedValue;
};
const DefaultTransformer = {
    ...Color.convert,
    ...Color,
};

/**
 * @internal
 *
 * Resolves token variables within derivations/transformations into the source value.
 *
 * @param variable a value that contains a variable that should be resolved to the source value
 * @param processedTokens - array of tokens that have been processed
 * @returns false if the token cannot be resolved, otherwise the resolved token value
 */
const resolveDerivationValue = (variable, processedTokens) => {
    const variableName = variable.replace(/[\{\}\!]/g, "");
    const resolvedVariable = processedTokens.find(({ name }) => name === variableName);
    if (!resolvedVariable) {
        return false;
    }
    if (/{![a-zA-Z0-9\-]+}/.test(resolvedVariable.value)) {
        return false;
    }
    return variable.replace(/{![a-zA-Z0-9\-]+}/, resolvedVariable.value);
};
/**
 * @internal
 *
 * Recursively processes and resolves tokens.
 *
 * @param unprocessedTokens an array of tokens that have yet to be processed
 * @param processedTokens an array of tokens that have been processed
 */
const processTokens$1 = (unprocessedTokens, processedTokens, customTransformers = {}) => {
    var _a, _b, _c, _d, _e;
    if (!(unprocessedTokens.length > 0)) {
        return processedTokens;
    }
    let newUnprocessedTokens = [];
    let newProcessedTokens = [...processedTokens];
    let noNewTokensProcessed = true;
    for (let t of unprocessedTokens) {
        // Extract variables
        const vars = extractVariables(t.value);
        // If there is a variable value try to resolve it
        if (vars.length > 0) {
            let newValue = t.value;
            let fullyResolved = true;
            for (let v of vars) {
                const variableName = v.replace(/[\{\}\!]/g, "");
                const resolvedVariable = processedTokens.find(({ name }) => name === variableName);
                if (resolvedVariable) {
                    newValue = newValue.replace(new RegExp(v, "g"), resolvedVariable.value);
                }
                else {
                    fullyResolved = false;
                }
            }
            (_b = (_a = t.derivation) === null || _a === void 0 ? void 0 : _a.transform) === null || _b === void 0 ? void 0 : _b.forEach((t) => {
                Object.entries(t).forEach(([transformName, transformValue]) => {
                    if (!transformValue)
                        return;
                    const newTransform = { [transformName]: {} };
                    Object.entries(transformValue).forEach(([name, value]) => {
                        if (typeof value === "string" && /{![a-zA-Z0-9\-]+}/.test(value)) {
                            const res = resolveDerivationValue(value, processedTokens);
                            if (res === false) {
                                fullyResolved = false;
                                return;
                            }
                            newTransform[transformName][name] = res;
                            return;
                        }
                        else if (Array.isArray(value)) {
                            const newValueArray = [];
                            value.forEach((v) => {
                                // Push values that aren't variables
                                if (!/{![a-zA-Z0-9\-]+}/.test(v)) {
                                    newValueArray.push(v);
                                    return;
                                }
                                const res = resolveDerivationValue(v, processedTokens);
                                if (res === false) {
                                    fullyResolved = false;
                                    return;
                                }
                                newValueArray.push(res);
                            });
                            newTransform[transformName][name] = newValueArray;
                            return;
                        }
                        newTransform[transformName][name] = value;
                    });
                });
            });
            // If the variable has not been resolved yet push it to the unresolved array
            // else push it to the resolved array
            if (!fullyResolved) {
                newUnprocessedTokens.push({ ...t, value: newValue });
            }
            else {
                noNewTokensProcessed = false;
                const token = {
                    ...t,
                    value: resolveTransformation({ ...t, value: newValue }, customTransformers),
                    derivation: { value: t.value, ...t.derivation },
                };
                if (!((_c = token.derivation) === null || _c === void 0 ? void 0 : _c.transform)) {
                    (_d = token.derivation) === null || _d === void 0 ? true : delete _d.transform;
                }
                newProcessedTokens.push(token);
            }
        }
        else {
            noNewTokensProcessed = false;
            const token = t;
            // If there is a value for the derivation key, maintain the derivation
            if (!!((_e = t.derivation) === null || _e === void 0 ? void 0 : _e.value)) {
                token.derivation = t.derivation;
            }
            if (token.derivation && Object.keys(token.derivation).length === 0) {
                delete token.derivation;
            }
            newProcessedTokens.push(token);
        }
    }
    if (noNewTokensProcessed) {
        return processedTokens;
    }
    return resolveOverrides(processTokens$1(newUnprocessedTokens, newProcessedTokens, customTransformers));
};

/**
 * @internal
 *
 * Flattens all tokens into a single array.
 *
 * @param input token input object
 */
const resolveImports = (input) => {
    var _a, _b;
    let tokens = [];
    // Add current file's tokens to the list
    tokens = [...((_a = input.tokens) !== null && _a !== void 0 ? _a : [])];
    // Add in new imports if necessary
    (_b = input.imports) === null || _b === void 0 ? void 0 : _b.forEach((i) => {
        const t = resolveImports(i);
        tokens = [...tokens, ...t];
    });
    return tokens;
};

/**
 * Converts instances of TokenStudio variable syntax into
 * the syntax that `@sas/design-tokenizer` expects.
 *
 * @returns token with converted variable syntax
 *
 * @example
 * ```
 * convertVariableSyntax('{color.background.frame.1}') // -> '{!color-background-frame-1}'
 * ```
 */
const convertVariableSyntax = (token) => {
    // Variables in the `{my-token-name}` format are errors
    if (new RegExp(/\{([\w\d\-]+)\}/, "gi").test(token)) {
        throw Error(`Incorrect variable syntax: ${token}`);
    }
    const varRegex = new RegExp(/\{([\w\d\-\.]+)\}/, "gi");
    if (varRegex.test(token)) {
        return `{!${token.replace(varRegex, "$1").replace(/\./g, "-")}}`;
    }
    return token;
};
/**
 * Converts instances of CSS custom properties into `@sas/design-tokenizer`
 * variable syntax.
 *
 * @returns an array of tokens with their CSS custom properties converted
 */
const convertCssVars = (tokens) => {
    const resolvedTokens = {};
    tokens.forEach((token) => (resolvedTokens[token.name] = token));
    const varsRegex = new RegExp(/var\(--([\d\w]+[\d\w\-]*)\)/);
    while (
    // Continue running when there are tokens that contain variables
    Object.values(resolvedTokens).some((token) => varsRegex.test(token.value))) {
        Object.values(resolvedTokens).forEach((token) => {
            if (!varsRegex.test(token.value)) {
                return;
            }
            const res = varsRegex.exec(token.value);
            if (res && resolvedTokens[res[1]]) {
                token.value = token.value.replace(`var(--${res[1]})`, `{!${res[1]}}`);
            }
        });
    }
    return Object.values(resolvedTokens);
};
const isTokenValue = (t) => {
    return (typeof t === "object" &&
        t.hasOwnProperty("value") &&
        t.hasOwnProperty("type"));
};
/**
 * Recursively parses a deep object and prepends the key at each level.
 *
 * @example
 * ```
 * parseObject({
 *    "color": {
 *      "background": {
 *        "frame": {
 *          "1": {
 *            value: "#fff",
 *            category: "color"
 *          }
 *        }
 *      }
 *    }
 * }); // -> [{name: "color-background-frame-1", value: "#fff", category: "color"}]
 * ```
 */
const parseObject = (obj, prefix = "") => {
    let tokens = [];
    Object.entries(obj).forEach(([k, token]) => {
        const tokenName = prefix ? `${prefix}-${k}` : k;
        if (isTokenValue(token)) {
            tokens.push({
                name: tokenName,
                value: convertVariableSyntax(token.value),
                category: token.type,
            });
            return;
        }
        tokens = [...tokens, ...parseObject(token, tokenName)];
    });
    return tokens;
};
/**
 * Apply derivations to tokens.
 */
const applyDerivations = (tokens, derivations) => {
    if (!derivations)
        return tokens;
    return tokens.map((token) => {
        if (derivations.hasOwnProperty(token.name)) {
            token.derivation = derivations[token.name];
        }
        return token;
    });
};
const convertTokens = (entry, derivations = {}, key) => {
    var _a, _b;
    return {
        name: entry.name,
        selectors: (_a = entry.selectors) !== null && _a !== void 0 ? _a : [],
        imports: Object.values((_b = entry.imports) !== null && _b !== void 0 ? _b : {}).map((i) => {
            return convertTokens(i, derivations, key);
        }),
        tokens: convertCssVars(applyDerivations(parseObject(entry.tokens), derivations[key])),
    };
};
const isDerivations = (key, _value) => {
    return key === "_derivations";
};
const parseTokenStudioInput = (manifest) => {
    const entrypoints = { entrypoints: [] };
    Object.entries(manifest).forEach(([k, v]) => {
        if (isDerivations(k))
            return;
        entrypoints.entrypoints.push({
            path: v.path,
            name: k,
            selectors: v.selectors,
            tokens: convertTokens(v, manifest._derivations, k),
        });
    });
    return entrypoints;
};

const isEntrypoint = (e) => e.hasOwnProperty("entrypoints");
/**
 * Resolves token object to a processed tokens array. During processing,
 * variables are resolved, transformations are applied, and derivations
 * are updated (where applicable). The resulting processed file may be
 * passed to the generator.
 *
 * @param entrypoint - resolved entry file with imported token files resolved
 * @param customTransformers - custom transformers required to process the tokens
 */
const processor = (entrypoint, customTransformers) => {
    var _a, _b;
    const entry = isEntrypoint(entrypoint)
        ? entrypoint
        : parseTokenStudioInput(entrypoint);
    console.info("Using the following entrypoint:\n", entry);
    // Resolve imports
    const tokens = [];
    for (let f of entry.entrypoints) {
        const t = resolveImports(f.tokens);
        tokens.push({
            name: (_a = f.name) !== null && _a !== void 0 ? _a : getTokenName(f.path),
            selectors: (_b = f.selectors) !== null && _b !== void 0 ? _b : [],
            unprocessedTokens: t,
            processedTokens: [],
        });
    }
    const finalTokens = [];
    // Process tokens
    for (let i in tokens) {
        const processedTokens = processTokens$1(tokens[i].unprocessedTokens, [], customTransformers);
        // Announce any missing tokens
        const missingTokens = calculateMissingTokens(tokens[i].unprocessedTokens, processedTokens);
        if (missingTokens.length > 0) {
            throw Error(`The following variables cannot be resolved: ${missingTokens
                .map((v) => `"${v.value}"`)
                .join(", ")}`);
        }
        finalTokens.push({
            name: tokens[i].name,
            selectors: tokens[i].selectors,
            tokens: processedTokens,
        });
        if (customTransformers) {
            finalTokens[finalTokens.length - 1].customTransformers = customTransformers;
        }
    }
    return finalTokens;
};

/**
 * @internal
 *
 * Categorizes tokens based on their name.
 */
const categorize = (tokens) => {
    return tokens.sort((a, b) => (a.name < b.name ? -1 : 1));
};
/**
 * @internal
 *
 * Categorizes tokens based on their name.
 */
const categorizeTokens = {
    name: "categorizeTokens",
    fn: categorize,
};

/**
 * @internal
 *
 * Convert string to camel case.
 *
 * Example
 * ```
 * my-token => myToken
 * ```
 */
const toCamelCase = {
    name: "toCamelCase",
    fn: (n) => {
        const o = n
            .replace(/\s+/g, "")
            .replace(/[\.\-]([a-zA-Z0-9])/g, (_, match) => match.toUpperCase());
        return o.slice(0, 1).toLowerCase() + o.slice(1);
    },
};

/**
 * @internal
 *
 * Convert string to camel case.
 *
 * Example
 * ```
 * my-token => myToken
 * ```
 */
const toUpperCamelCase = {
    name: "toUpperCamelCase",
    fn: (n) => {
        const o = n
            .replace(/\s+/g, "-")
            .replace(/[\.\-]([a-zA-Z0-9])/g, (_, match) => match.toUpperCase());
        return o.slice(0, 1).toUpperCase() + o.slice(1);
    },
};

/**
 * @internal
 *
 * Escape quote characters in a string.
 */
const escapeQuotes = {
    name: "escapeQuotes",
    fn: (variable) => {
        return variable.replace(/(["])/g, "\\$1");
    },
};

/**
 * @internal
 *
 * Check if two values are equal. Uses `==` to check equality.
 */
const eq = {
    name: "eq",
    fn: (value1, value2) => {
        return value1 == value2;
    },
};

/**
 * @internal
 *
 * Check if two values are strictly equal (including type equivalence). Uses `===` to check equality.
 */
const eqStrict = {
    name: "eqStrict",
    fn: (value1, value2) => {
        return value1 === value2;
    },
};

/**
 * @internal
 *
 * Add indentations to hbs code.
 */
const indent = {
    name: "indent",
    fn: (n, suffix) => {
        return `${"\t".repeat(n)}${suffix}`;
    },
};

const createTokenObject = (tokenArray) => {
    const tokenObj = {};
    tokenArray.tokens.forEach((t) => {
        tokenObj[t.name] = t;
    });
    return tokenObj;
};
function generate(
/**
 * Processed tokens
 */
input, 
/**
 * Path to Handlebars template
 */
template, 
/**
 * Custom helpers that need to be registered to support the template
 */
helpers = []) {
    // Set up Handlebars
    const compiledTemplate = Handlebars.compile(template);
    // Register any helpers
    const basicHelpers = [
        categorizeTokens,
        toCamelCase,
        toUpperCamelCase,
        escapeQuotes,
        indent,
        eq,
        eqStrict,
        ...helpers,
    ];
    basicHelpers.forEach((h) => {
        Handlebars.registerHelper(h.name, h.fn);
    });
    // Process all inputs if passed as an array
    if (Array.isArray(input)) {
        return input.map((i) => {
            return compiledTemplate({ ...i, tokenObject: createTokenObject(i) });
        });
    }
    return compiledTemplate({ ...input, tokenObject: createTokenObject(input) });
}

/**
 * @internal
 *
 * Takes a list of tokens and applies color overrides
 *
 * @param tokens array of tokens
 * @param overrides color overrides
 */
const applyOverrides = (tokens, overrides) => {
    var _a;
    const tokenOverrides = [...tokens];
    for (let t of tokenOverrides) {
        if (t.name in overrides) {
            t.value = (_a = overrides[t.name]) !== null && _a !== void 0 ? _a : t.value;
            if (t.derivation)
                t.derivation = {};
        }
    }
    return tokenOverrides;
};

/**
 * @internal
 *
 * Recursively processes and resolves tokens.
 *
 * @param unprocessedTokens array of tokens that have yet to be processed
 * @param processedTokens array of tokens that have been processed
 */
const processTokens = (unprocessedTokens, processedTokens = [], transformers = {}) => {
    var _a, _b;
    // Return processed tokens if there are no tokens left to process
    if (unprocessedTokens.length <= 0) {
        return processedTokens;
    }
    let newUnprocessedTokens = [];
    let newProcessedTokens = [...processedTokens];
    let noNewTokensProcessed = true;
    for (let t of unprocessedTokens) {
        // Extract variables
        const value = t.value;
        const vars = extractVariables(value);
        let newValue = value;
        let fullyResolved = true;
        if (vars.length > 0) {
            for (let v of vars) {
                const variableName = v.replace(/[\{\}\!]/g, "");
                const resolvedVariable = processedTokens.find(({ name }) => name === variableName);
                if (resolvedVariable) {
                    newValue = newValue.replace(new RegExp(v, "g"), resolvedVariable.value);
                }
                else {
                    fullyResolved = false;
                }
            }
        }
        // Attempt to resolve any variables in transformations
        const resolvedTransform = [];
        (_b = (_a = t.derivation) === null || _a === void 0 ? void 0 : _a.transform) === null || _b === void 0 ? void 0 : _b.forEach((t) => {
            Object.entries(t).forEach(([transformName, transformValue]) => {
                if (!transformValue)
                    return;
                const newTransform = { [transformName]: {} };
                Object.entries(transformValue).forEach(([name, value]) => {
                    if (typeof value === "string" && /{![a-zA-Z0-9\-]+}/.test(value)) {
                        const res = resolveDerivationValue(value, processedTokens);
                        if (res === false) {
                            fullyResolved = false;
                            return;
                        }
                        newTransform[transformName][name] = res;
                        return;
                    }
                    else if (Array.isArray(value)) {
                        const newValueArray = [];
                        value.forEach((v) => {
                            // Push values that aren't variables
                            if (!/{![a-zA-Z0-9\-]+}/.test(v)) {
                                newValueArray.push(v);
                                return;
                            }
                            const res = resolveDerivationValue(v, processedTokens);
                            if (res === false) {
                                fullyResolved = false;
                                return;
                            }
                            newValueArray.push(res);
                        });
                        newTransform[transformName][name] = newValueArray;
                        return;
                    }
                    newTransform[transformName][name] = value;
                });
                // Add transformation
                resolvedTransform.push(newTransform);
            });
        });
        // If variable is still undefined push it to unresolved and try resolving
        // again in the next iteration
        if (!fullyResolved) {
            newUnprocessedTokens.push({ ...t, value: newValue });
            continue;
        }
        noNewTokensProcessed = false;
        // Convert derivation into a transform
        // Derivations are not handled during generation
        const newToken = {
            ...t,
            value: resolveTransformation({
                ...t,
                value: newValue,
                derivation: { ...t.derivation, transform: resolvedTransform },
            }, transformers),
        };
        newProcessedTokens.push(newToken);
    }
    if (noNewTokensProcessed) {
        return processedTokens;
    }
    return processTokens(newUnprocessedTokens, newProcessedTokens, transformers);
};

/**
 * Apply customizations to a processed token object. Overrides
 * will be applied and derivations will be run to retried the
 * final values of the tokens.
 *
 * @param tokens - object containing processed tokens
 * @param overrides - object of key/value pairs of overrides
 * @param customTransformers - an object containing any custom transformers required to complete derivations
 */
const customize = (tokens, overrides, customTransformers = {}) => {
    const customizedTokens = [];
    // Create a deep copy of the object so this code does not operate directly on the input object
    const tokensCopy = JSON.parse(JSON.stringify(tokens));
    tokens.forEach((t) => {
        if (!t.hasOwnProperty("customTransformers"))
            return;
        const tokens = tokensCopy.find((to) => to.name === t.name);
        if (!tokens)
            return;
        tokens["customTransformers"] = t["customTransformers"];
    });
    for (let tokenFile of tokensCopy) {
        // Update the tokens to reference the derivation value if it exists
        tokenFile.tokens.forEach((t) => {
            var _a;
            if ((_a = t.derivation) === null || _a === void 0 ? void 0 : _a.value) {
                t.value = t.derivation.value;
            }
        });
        const processedTokens = processTokens(applyOverrides(tokenFile.tokens, overrides), [], {
            ...tokenFile.customTransformers,
            ...customTransformers,
        });
        customizedTokens.push({
            name: tokenFile.name,
            selectors: tokenFile.selectors,
            tokens: processedTokens,
        });
        if (tokenFile.customTransformers || customTransformers) {
            customizedTokens[customizedTokens.length - 1].customTransformers = {
                ...tokenFile.customTransformers,
                ...customTransformers,
            };
        }
    }
    return customizedTokens;
};

const TransformSchema = z
    .object({
    setAlpha: z.object({ value: z.number() }).optional(),
    lighten: z.object({ value: z.number() }).optional(),
    darken: z.object({ value: z.number() }).optional(),
    change: z.object({ property: z.string(), value: z.number() }).optional(),
    scale: z.object({ property: z.string(), value: z.number() }).optional(),
    adjust: z.object({ property: z.string(), value: z.number() }).optional(),
    alphaBlend: z
        .object({ background: z.string(), alpha: z.number() })
        .optional(),
    decreaseContrast: z
        .object({ background: z.string(), amount: z.number() })
        .optional(),
    adjustLumaDelta: z
        .object({
        background: z.string(),
        amount: z.number(),
        invert: z.boolean().optional(),
        useHSL: z.boolean().optional(),
    })
        .optional(),
    decreaseLumaDelta: z
        .object({
        background: z.string(),
        amount: z.number(),
        useHSL: z.boolean().optional(),
    })
        .optional(),
    increaseLumaDelta: z
        .object({
        background: z.string(),
        amount: z.number(),
        useHSL: z.boolean().optional(),
    })
        .optional(),
    grayscale: z.object({ method: z.string().optional() }).optional(),
    invertLuminance: z.object({}).optional(),
    maxLumaContrast: z.object({ colors: z.array(z.string()) }).optional(),
    relativeLuminance: z.object({}).optional(),
    interpolate: z
        .object({ background: z.string(), alpha: z.number() })
        .optional(),
    hex2rgb: z.object({}).optional(),
    hsl2hsv: z.object({}).optional(),
    hsl2rgb: z.object({}).optional(),
    hsv2hsl: z.object({}).optional(),
    rgb2hex: z.object({}).optional(),
    rgb2hsl: z.object({}).optional(),
    rgba2hsla: z.object({}).optional(),
    toHSL: z.object({}).optional(),
    toHSLA: z.object({}).optional(),
    toRGB: z.object({}).optional(),
    toRGBA: z.object({}).optional(),
    add: z
        .object({ value: z.number().optional(), unit: z.string().optional() })
        .optional(),
    divide: z
        .object({ value: z.number().optional(), unit: z.string().optional() })
        .optional(),
    multiply: z
        .object({ value: z.number().optional(), unit: z.string().optional() })
        .optional(),
    subtract: z
        .object({ value: z.number().optional(), unit: z.string().optional() })
        .optional(),
})
    .strict();
const TokenStudioManifestDerivations = z.object({
    _derivations: z.record(z.string(), z.record(z.string(), z
        .object({
        value: z.string(),
        transform: z.array(TransformSchema).optional(),
    })
        .strict())),
});
const TokenStudioManifestThemes = z
    .object({
    path: z.string(),
    selectors: z.array(z.string()).optional(),
    imports: z.array(z.string()).optional(),
    title: z.string(),
    description: z.string(),
})
    .strict();
const TokenStudioTokens = z.record(z.string(), z.union([
    z.lazy(() => TokenStudioTokens),
    z
        .object({
        value: z.string(),
        type: z.string(),
        metadata: z
            .record(z.union([z.number(), z.string(), z.boolean(), z.null()]))
            .optional(),
        description: z.string().optional(),
    })
        .strict(),
]));
const Entrypoint = z
    .object({
    path: z.string(),
    name: z.string(),
    selectors: z.array(z.string()).optional(),
})
    .strict();
const EntrySchema = z
    .object({
    entrypoints: z.array(Entrypoint),
})
    .strict();
const TokenSchema = z
    .object({
    title: z.string(),
    description: z.string().optional(),
    selectors: z.array(z.string()).optional(),
    imports: z.array(z.string()).optional(),
    tokens: z.array(z.object({
        name: z.string(),
        value: z.string(),
        category: z.string().optional(),
        derivation: z
            .object({
            value: z.string().optional(),
            transform: z.array(TransformSchema).optional(),
        })
            .optional(),
    })),
})
    .strict();
/**
 * @internal
 *
 * Run actual schema validation.
 */
const handleValidation = (obj, schema) => {
    return schema.safeParse(obj);
};
/**
 * @internal
 *
 * Format schema validation errors in a more predictable manner.
 */
const formatError = (res) => {
    if (res.success)
        return "";
    return res.error.issues
        .map((e) => `['${e.path.join("->")}']: ${e.message}`)
        .join("\n");
};
/**
 * Validate object's schema to ensure it maintains the expected structure.
 *
 * @param content object to be validated against schema
 * @param options.isEntry whether to validate against the entry schema or token schema
 */
const validateSchema = (content, options) => {
    let errors;
    if (options === null || options === void 0 ? void 0 : options.isTokenStudio) {
        if (options === null || options === void 0 ? void 0 : options.isEntry) {
            let e = [];
            Object.entries(content).forEach(([k, v]) => {
                if (k === "_derivations") {
                    e.push(handleValidation({ _derivations: v }, TokenStudioManifestDerivations));
                }
                else {
                    const res = handleValidation(v, TokenStudioManifestThemes);
                    if (!res.success) {
                        res.error.issues.forEach((e) => {
                            e.path.unshift(k);
                        });
                    }
                    e.push(res);
                }
            });
            // Filter any successful validations and merge errors
            const e0 = e.filter((e) => !e.success);
            if (e0.length > 0) {
                errors = e0.reduce((prev, e, idx) => {
                    if (idx === 0)
                        return prev;
                    if (!prev.success && !e.success) {
                        prev.error.addIssues(e.error.issues);
                    }
                    return prev;
                }, e0[0]);
            }
            else {
                errors = e[0];
            }
        }
        else {
            errors = handleValidation(content, TokenStudioTokens);
        }
    }
    else {
        if (options === null || options === void 0 ? void 0 : options.isEntry) {
            errors = handleValidation(content, EntrySchema);
        }
        else {
            errors = handleValidation(content, TokenSchema);
        }
    }
    const errorMessage = formatError(errors);
    if (errorMessage.length > 0)
        throw Error(errorMessage);
};

/**
 * Validate a token's name for the proper syntax.
 *
 * @param name token name
 * @returns whether the name is valid or not
 */
const isWeakToken = (name) => {
    return /^[a-z_][a-z0-9]*(?:[\-\.\_][a-z0-9]+)*$/i.test(name);
};

/**
 * Validates that tokens adhere to a standard syntax. Tokens may also be
 * validated for custom, stronger syntax requirements if desired through
 * use of the `customValidator` callback.
 *
 * @param tokens list of tokens to validate
 * @param customValidator a custom validator if stronger validation is desired
 */
const validateTokens = (tokens, customValidator) => {
    const invalidTokens = [];
    for (let t of tokens.tokens || []) {
        // Ensure the token meets weak validation standards
        const pass = isWeakToken(t.name) &&
            (!!customValidator ? customValidator(t.name, t.value) : true);
        // Add tokens to invalid token array
        if (!pass) {
            invalidTokens.push(t.name);
        }
    }
    if (invalidTokens.length > 0) {
        throw Error(`Invalid tokens: ${invalidTokens.map((v) => `"${v}"`).join(", ")}`);
    }
};

/**
 * Validate JSON objects to ensure they are the proper format
 * that the tool expects of them.
 *
 * @param objects.contents - object that should be validated
 * @param objects.options.customTokenValidator - test tokens for custom syntax requirements
 * @param objects.options.isEntry - whether the object should be validated as an entry file or a token file
 * @param objects.options.isTokenStudio - whether the object should use Token Studio validation
 */
const validate = (objects) => {
    objects.forEach(({ content, options }) => {
        var _a, _b;
        validateSchema(content, {
            isEntry: (_a = options === null || options === void 0 ? void 0 : options.isEntry) !== null && _a !== void 0 ? _a : false,
            isTokenStudio: (_b = options === null || options === void 0 ? void 0 : options.isTokenStudio) !== null && _b !== void 0 ? _b : false,
        });
        if (!(options === null || options === void 0 ? void 0 : options.isEntry)) {
            validateTokens(content, options === null || options === void 0 ? void 0 : options.customTokenValidator);
        }
    });
};

var cssTemplate = `{{#each selectors}}{{{this}}}{{#unless @last}},
{{/unless}}{{else}}.{{name}}{{/each}} {
{{#each tokens}}
{{indent 1 ""}}--{{name}}: {{{value}}};
{{/each}}
}`;

var scssTemplate = `{{#each selectors}}{{{this}}}{{#unless @last}},
{{/unless}}{{else}}.{{name}}{{/each}} {
{{#each tokens}}
{{indent 1 ""}}--{{name}}: {{{value}}};
{{/each}}
}`;

var lessTemplate = `{{#each selectors}}{{{this}}}{{#unless @last}},
{{/unless}}{{else}}.{{name}}{{/each}} {
{{#each tokens}}
{{indent 1 ""}}--{{name}}: {{{value}}};
{{/each}}
}`;

var jsonTemplate = `{
{{indent 1 ""}}"name": "{{name}}",
{{indent 1 ""}}"selectors": [{{#each selectors}}"{{{escapeQuotes this}}}"{{#unless @last}}, {{/unless}}{{/each}}],
{{indent 1 ""}}"tokens": {{indent 0 "{"}}{{#with (categorizeTokens tokens)}}
{{#each this}}
{{indent 2 ""}}"{{name}}": "{{{escapeQuotes value}}}"{{#unless @last}},{{/unless}}
{{/each}}
{{/with}}{{indent 1 "}"}}
}`;

var jsTemplate = `export type {{{toUpperCamelCase name}}}TokensType = {
{{indent 1 ""}}readonly "name": string,
{{indent 1 ""}}readonly "selectors": string[],
{{indent 1 ""}}readonly "tokens": {{indent 0 "{"}}{{#with (categorizeTokens tokens)}}
{{#each this}}
{{indent 2 ""}}readonly "{{name}}": string{{#unless @last}},{{/unless}}
{{/each}}
{{/with}}{{indent 1 "}"}}
};

export const {{{toUpperCamelCase name}}}Tokens: {{{toUpperCamelCase name}}}TokensType = {
{{indent 1 ""}}"name": "{{name}}",
{{indent 1 ""}}"selectors": [{{#each selectors}}"{{{escapeQuotes this}}}"{{#unless @last}}, {{/unless}}{{/each}}],
{{indent 1 ""}}"tokens": {{indent 0 "{"}}{{#with (categorizeTokens tokens)}}
{{#each this}}
{{indent 2 ""}}"{{name}}": "{{{escapeQuotes value}}}"{{#unless @last}},{{/unless}}
{{/each}}
{{/with}}{{indent 1 "}"}}
};`;

/**
 * Array of allowed default generation types
 */
const DEFAULT_GENERATORS = [
    "all",
    "css",
    "scss",
    "less",
    "json",
    "js",
];

const templates = {
    css: cssTemplate,
    scss: scssTemplate,
    less: lessTemplate,
    json: jsonTemplate,
    js: jsTemplate,
};

export { DEFAULT_GENERATORS, createBarrelContent as barrel, index as colorize, customize as customizer, generate as generator, processor, templates, validate as validator };
//# sourceMappingURL=index.js.map
